import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { sendWelcomeEmail } from '@/lib/edge-functions'
import { getTenantConfig } from '@/lib/tenant-config'
import {
  Button,
  Card,
  CardContent,
  Skeleton,
} from '@lead-magnifier/ui'
import { CheckCircle2, XCircle, Loader2, Download } from 'lucide-react'
import confetti from 'canvas-confetti'

export const Route = createFileRoute('/confirm/$token/')({
  component: ConfirmPage,
})

type ConfirmState = 'loading' | 'confirming' | 'success' | 'already-confirmed' | 'error'

interface EventData {
  name: string
  downloadUrl: string
  ctaButtonText: string
  primaryColor: string
  backgroundColor: string
  logoUrl: string | null
}

function ConfirmPage() {
  const { token } = Route.useParams()
  const [state, setState] = useState<ConfirmState>('loading')
  const [event, setEvent] = useState<EventData | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    confirmLead()
  }, [token])

  const confirmLead = async () => {
    try {
      setState('confirming')

      // Find lead by DOI token
      const { data: lead, error: leadError } = await supabase
        .from('leads')
        .select(`
          id,
          email,
          doi_confirmed,
          event_id,
          events (
            name,
            download_url,
            cta_button_text,
            primary_color,
            background_color,
            logo_url
          )
        `)
        .eq('doi_token', token)
        .single()

      if (leadError || !lead) {
        setError('Ungültiger oder abgelaufener Bestätigungslink.')
        setState('error')
        return
      }

      const eventData = lead.events as {
        name: string
        download_url: string
        cta_button_text: string
        primary_color: string
        background_color: string
        logo_url: string | null
      }

      setEvent({
        name: eventData.name,
        downloadUrl: eventData.download_url,
        ctaButtonText: eventData.cta_button_text || 'Jetzt herunterladen',
        primaryColor: eventData.primary_color || '#3b82f6',
        backgroundColor: eventData.background_color || '#007E87',
        logoUrl: eventData.logo_url,
      })

      // Check if already confirmed
      if (lead.doi_confirmed) {
        setState('already-confirmed')
        return
      }

      // Confirm the lead
      const { error: updateError } = await supabase
        .from('leads')
        .update({
          doi_confirmed: true,
          doi_confirmed_at: new Date().toISOString(),
        })
        .eq('id', lead.id)

      if (updateError) {
        throw updateError
      }

      // Record activity
      await supabase.from('lead_activities').insert({
        lead_id: lead.id,
        activity_type: 'doi_confirmed',
        metadata: { confirmed_at: new Date().toISOString() },
      })

      // Send welcome email via Edge Function
      try {
        await sendWelcomeEmail(lead.id, lead.event_id, lead.email)
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError)
        // Continue anyway - confirmation is saved
      }

      // Success - show confetti
      setState('success')
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
          colors: [eventData.primary_color || '#3b82f6', '#22c55e', '#eab308'],
        })
      }, 100)
    } catch (err) {
      console.error('Confirm error:', err)
      setError('Fehler bei der Bestätigung. Bitte versuche es erneut.')
      setState('error')
    }
  }

  // Loading state
  if (state === 'loading' || state === 'confirming') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h1 className="text-xl font-semibold">Wird bestätigt...</h1>
            <p className="text-muted-foreground mt-2">Bitte warte einen Moment.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Bestätigung fehlgeschlagen</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Already confirmed state
  if (state === 'already-confirmed') {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(135deg, ${event?.backgroundColor || '#007E87'} 0%, ${event?.primaryColor || '#3b82f6'} 100%)`,
        }}
      >
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            {event?.logoUrl && (
              <img
                src={event.logoUrl}
                alt=""
                className="h-16 w-16 rounded-full bg-white shadow-lg object-contain p-2 mx-auto mb-4"
              />
            )}
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Bereits bestätigt</h1>
            <p className="text-muted-foreground mb-6">
              Du hast deine E-Mail-Adresse bereits bestätigt.
            </p>
            {event?.downloadUrl && (
              <Button asChild className="w-full" size="lg">
                <a href={event.downloadUrl} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  {event.ctaButtonText}
                </a>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  // Success state
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${event?.backgroundColor || '#007E87'} 0%, ${event?.primaryColor || '#3b82f6'} 100%)`,
      }}
    >
      <Card className="w-full max-w-md shadow-2xl">
        <CardContent className="p-8 text-center">
          {event?.logoUrl && (
            <img
              src={event.logoUrl}
              alt=""
              className="h-20 w-20 rounded-full bg-white shadow-lg object-contain p-2 mx-auto mb-6"
            />
          )}
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Vielen Dank!</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Deine E-Mail-Adresse wurde erfolgreich bestätigt.
          </p>
          {event?.downloadUrl && (
            <Button
              asChild
              className="w-full"
              size="lg"
              style={{ backgroundColor: event?.primaryColor }}
            >
              <a href={event.downloadUrl} target="_blank" rel="noopener noreferrer">
                <Download className="h-5 w-5 mr-2" />
                {event.ctaButtonText}
              </a>
            </Button>
          )}
          <p className="text-center text-xs text-muted-foreground mt-6">
            Powered by {getTenantConfig().branding.name}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
